/**
 * 東京都の料理12品。レベルが上がるほど大きく、器つきの汁物・鍋物になる。
 *
 * 選定のときに迷った点を残しておく。
 * - 「かんぴょう巻き」は栃木にすでに置いてあるので外した。
 * - 「玉子焼き」は兵庫の明石焼（玉子焼）と名前が重なるので外した。
 * - にぎりずし（江戸前ずし）よりも、八丈島の「島ずし」のほうが絵として他県と重ならない。
 *   握りの上に黄色い練りがらしがのるのが公的な出典に書かれた決定的な特徴。
 * - 串だんごは関東では4つ刺しで、関西の5つ刺しとは違う。4つで描く。
 * - 桜もちは関東風。焼いた薄い皮を二つ折りにし、塩漬けの桜の葉2〜3枚で包む。
 *   関西の道明寺（もち米のつぶつぶ）とは別物。
 * - 「治助芋」は2012年に商標登録された名称なので、品名としては使わず「治助芋のネギ味噌」という
 *   公的な出典どおりの料理名のままにしてある。
 *
 * 2026-10-02 確定案（audit/_確定案.csv）で料理を入れ替えた。
 * - 外した品：佃煮・治助芋のネギ味噌・こはだの粟づけ・桜もち・いもようかん・べったら漬け（店で食べられる品に差し替え）
 * - 亀戸のくず餅：店名・創業年は書かない（出典は観光協会のページだが、中身は1店の商品・会員紹介）
 * - 亀戸のくず餅：「和菓子唯一の発酵食品」は書かない（店の自称。観光土産品ページの「和菓子では珍しい発酵食品」の表現にとどめた）
 * - 亀戸のくず餅：発酵の日数（450日）は書かない（店ごとの製法で、料理一般の事実として確かめられない）
 * - あんみつ：【暫定】公式の出典が見つかっていない。説明は絵の内容に沿った一般的な記述だけにした。出典が見つかるまで仮の文（2026-10-02）
 * - 油そば：発祥の店・発祥の地は書かない（諸説あり、公式で確かめられない）
 * - 油そば：【暫定】公式の出典が見つかっていない。説明は絵の内容に沿った一般的な記述だけにした。出典が見つかるまで仮の文（2026-10-02）
 * - くさや：においの強さを売りにする書き方はしない（出典は「普通の干物の旨味が増したよう」とうまみの側から書いている）
 * - くさや：どの島が本場かは書かない（出典は「伊豆諸島の7つの町村で生産」とだけ書き、島名を挙げていない）
 * - 浅草天丼：老舗の店名・創業年は書かない
 * - どぜう鍋：「最も古い店」「創業1801年」と店名は書かない（農水省のページにはあるが、店の宣伝になるため）
 * - どぜう鍋：「どぜう」という表記が店の屋号から来たという話は書かない（特定の店の話になるため）
 */

import { attachHulls } from '../../utils/itemShape';
import type { RewardLink, StageDefinition, StageItemDefinition } from '../types';
import { tokyoSceneryTour } from './tokyoScenes';
import { TOKYO_FOOD_HULLS } from './tokyoFoodShapes.generated';

function searchLinks(area: string, dish: string): RewardLink[] {
  return [
    {
      label: '地図で探す',
      url: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${area} ${dish}`)}`,
    },
    {
      label: 'お店を調べる',
      url: `https://www.google.com/search?q=${encodeURIComponent(`${area} ${dish}`)}`,
    },
  ];
}

const FOOD_ITEMS: StageItemDefinition[] = [
  {
    id: 'kushi-dango',
    level: 1,
    name: '串だんご',
    shortName: '串だんご',
    image: '/assets/foods/tokyo/01_kushi_dango.webp',
    radius: 17,
    mass: 1,
    score: 2,
    description: '関東の串だんごは一串4つ刺し。江戸では4文銭を置いて持ち帰る客が増えて店主が困り、苦肉の策でだんごの数を減らして4つにしたと伝えられる。関西は5つ刺しが主流。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/34_29_tokyo.html',
    icon: { label: '串だんご', shape: 'dumpling', baseColor: '#f2ece0', accentColor: '#7a4a1a' },
    reward: {
      unlockScore: 200,
      area: '台東区',
      headline: '関東は4つ刺し',
      tip: '団子は平安時代に唐から伝わった唐菓子のひとつ「団喜」が変わったものといわれる。江戸時代に庶民も旅をするようになると、茶店や寺社の境内で串団子を売る店ができた。',
      links: searchLinks('台東区', '串だんご'),
    },
  },
  {
    id: 'kameido-kuzumochi',
    level: 2,
    name: '亀戸のくず餅',
    shortName: 'くず餅',
    image: '/assets/foods/tokyo/02_kameido_kuzumochi.webp',
    radius: 21,
    mass: 1.3,
    score: 4,
    description: '和菓子では珍しい発酵食品で、独特の弾力と歯ごたえがある。香ばしいきな粉と黒蜜をたっぷりかけて食べる。亀戸で江戸時代から200年以上作り続けられている。',
    source:
      'https://www.koto-kanko.jp/goods/detail.php?goods_id=GD00044',
    icon: { label: 'くず餅', shape: 'mochi', baseColor: '#e8e2d0', accentColor: '#3b2414' },
    reward: {
      unlockScore: 500,
      area: '江東区亀戸',
      headline: '発酵させてつくる和菓子',
      tip: '小麦のでん粉を長い期間かけて乳酸発酵させ、それを蒸し上げて作る。関東流のくず餅として紹介されている。',
      links: searchLinks('江東区亀戸', 'くず餅'),
    },
  },
  {
    id: 'anmitsu',
    level: 3,
    name: 'あんみつ',
    shortName: 'あんみつ',
    image: '/assets/foods/tokyo/03_anmitsu.webp',
    radius: 26,
    mass: 1.7,
    score: 8,
    description: 'さいの目に切った寒天に、こしあん・求肥・赤えんどう豆・果物をのせ、黒みつをかけて食べる甘味。透きとおった寒天と黒みつの色の取り合わせが目を引く。',
    icon: { label: 'あんみつ', shape: 'berry', baseColor: '#d8e4ea', accentColor: '#6b2a2a' },
    reward: {
      unlockScore: 1000,
      area: '台東区（上野・浅草）',
      headline: '寒天に黒みつとあんこ',
      tip: 'アイスクリームやホイップクリームをのせたものもあるが、寒天とあんと黒みつが基本の組み合わせ。',
      links: searchLinks('台東区', 'あんみつ'),
    },
  },
  {
    id: 'abura-soba',
    level: 4,
    name: '油そば',
    shortName: '油そば',
    image: '/assets/foods/tokyo/04_abura_soba.webp',
    radius: 30,
    mass: 2.2,
    score: 16,
    description: 'スープを張らない汁なしのそば。太めの麺を、器の底のしょうゆだれと香味油によく混ぜて食べる。チャーシュー・メンマ・なると・刻みねぎをのせる。',
    icon: { label: '油そば', shape: 'ramen', baseColor: '#d9a85a', accentColor: '#3a2a1a' },
    reward: {
      unlockScore: 1800,
      area: '武蔵野市ほか多摩地域',
      headline: 'スープのないそば',
      tip: '食べる前に器の底からたれと油を麺にからめる。好みで酢やラー油を足して味を変える。',
      links: searchLinks('武蔵野市', '油そば'),
    },
  },
  {
    id: 'kusaya',
    level: 5,
    name: 'くさや',
    shortName: 'くさや',
    image: '/assets/foods/tokyo/05_kusaya.webp',
    radius: 36,
    mass: 2.8,
    score: 32,
    description: 'ムロアジやトビウオなどの新鮮な魚を開いて内臓を取り、「くさや液」という発酵液に漬けてから干す、伊豆諸島特産の干物。かめばかむほどうまみが出る。',
    source:
      'https://www.chiikishigen.metro.tokyo.lg.jp/introduction/details/introduction_18.html',
    icon: { label: 'くさや', shape: 'fish', baseColor: '#a08a6a', accentColor: '#4a4a4a' },
    reward: {
      unlockScore: 3000,
      area: '伊豆諸島',
      headline: '島の発酵液に漬けた干物',
      tip: '江戸時代中期から作られている。島では塩が貴重で、使った塩水をくり返し使ううちに発酵が進み、独特の風味になったと考えられている。',
      links: searchLinks('八丈島', 'くさや'),
    },
  },
  {
    id: 'udo-sumiso',
    level: 6,
    name: 'うどの酢味噌和え',
    shortName: 'うどの酢味噌和え',
    image: '/assets/foods/tokyo/06_udo_sumiso.webp',
    radius: 42,
    mass: 3.5,
    score: 64,
    description: 'うどは深さ3〜4メートルの「室」という穴で育ち、光が当たらないので白く、繊維も柔らかくあくも少ない。厚めに皮をむいて酢水につけ、短冊切りにして熱湯にくぐらせ冷やし、酢味噌で和える。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/34_30_tokyo.html',
    icon: { label: 'うどの酢味噌和え', shape: 'curry-plate', baseColor: '#f4f2e6', accentColor: '#e8dc9c' },
    reward: {
      unlockScore: 4500,
      area: '立川市／小平市',
      headline: '穴の中で育つから白い',
      tip: 'うどは数少ない日本原産の野菜のひとつで、江戸時代の後期から東京で栽培されてきた。室の入口は人がひとり入れるくらいの狭さで、その中で植え付けから収穫までを行う。',
      links: searchLinks('立川市', 'うどの酢味噌和え'),
    },
  },
  {
    id: 'monja-yaki',
    level: 7,
    name: 'もんじゃ焼き',
    shortName: 'もんじゃ焼き',
    image: '/assets/foods/tokyo/07_monja_yaki.webp',
    radius: 46,
    mass: 4.4,
    score: 128,
    description: '小麦粉を水で溶いた生地に、キャベツやコーンや揚げ玉を入れて鉄板で焼く。生地の外側から少しずつすくい、鉄板に押し当てて焦がして食べるのが一般的。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/34_3_tokyo.html',
    icon: { label: 'もんじゃ焼き', shape: 'curry-plate', baseColor: '#8a6032', accentColor: '#4c7a2a' },
    reward: {
      unlockScore: 6500,
      area: '中央区月島',
      headline: '鉄板に文字を書いて遊んだ',
      tip: '紙や習字の道具をなかなか手に入れられなかった子どもたちに、水で溶いた生地で鉄板に文字を書いて教えたり遊んだりしていたことから「文字焼き」と呼ばれ、それが「もんじゃ」に変わっていった。',
      links: searchLinks('中央区', 'もんじゃ焼き'),
    },
  },
  {
    id: 'fukagawa-meshi',
    level: 8,
    name: '深川めし',
    shortName: '深川めし',
    image: '/assets/foods/tokyo/08_fukagawa_meshi.webp',
    radius: 51,
    mass: 5.4,
    score: 256,
    description: '元来の形は、ねぎと生のアサリを味噌でさっと煮て、汁ごとご飯にかけたもの。江戸時代、大横川の一部は深川浦と呼ばれ、潮が引くと砂州が広がってアサリやハマグリ、アオヤギが豊富にとれる漁師町だった。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/34_1_tokyo.html',
    icon: { label: '深川めし', shape: 'rice-bowl', baseColor: '#d9c8a0', accentColor: '#6b8a3a' },
    reward: {
      unlockScore: 9000,
      area: '江東区',
      headline: '汁ごとご飯にかける',
      tip: '殻から外した身だけを売る「ぬきみ」という慣行から炊き込みご飯が生まれ、熱い汁をかけて食べる汁かけ飯も江戸の食べ方として定着した。いまは「ぶっかけ」と「炊き込み」の2種類がある。',
      links: searchLinks('江東区', '深川めし'),
    },
  },
  {
    id: 'asakusa-tendon',
    level: 9,
    name: '浅草天丼',
    shortName: '浅草天丼',
    image: '/assets/foods/tokyo/09_asakusa_tendon.webp',
    radius: 57,
    mass: 6.6,
    score: 512,
    description: '魚介や野菜に衣をつけて揚げる江戸のてんぷらを、甘辛い味のつゆでさっと煮て、汁ごとご飯にのせた丼。江戸前の魚介をごま油で揚げるのが江戸のてんぷらの特色とされる。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/34_6_tokyo.html',
    icon: { label: '天丼', shape: 'rice-bowl', baseColor: '#8a5a2b', accentColor: '#e2b46a' },
    reward: {
      unlockScore: 12000,
      area: '台東区浅草',
      headline: '甘辛いつゆをくぐった天ぷら',
      tip: '江戸では屋台で立ち食いする外食として広まった。関東大震災で職を失った職人が各地に移り住み、江戸前のてんぷらが全国に広まった。',
      links: searchLinks('浅草', '天丼'),
    },
  },
  {
    id: 'shima-zushi',
    level: 10,
    name: '島ずし',
    shortName: '島ずし',
    image: '/assets/foods/tokyo/10_shima_zushi.webp',
    radius: 63,
    mass: 8,
    score: 1024,
    description: '伊豆諸島でとれた魚を薄く切って醤油ベースの液に漬け、やや甘めの酢飯で握る。わさびが手に入らなかったため、代わりに練りがらしをのせるのが特徴。八丈島や小笠原ではトビウオやメダイ、サワラも使う。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/34_19_tokyo.html',
    icon: { label: '島ずし', shape: 'sashimi-platter', baseColor: '#7a4a2a', accentColor: '#e8c020' },
    reward: {
      unlockScore: 16000,
      area: '八丈町',
      headline: 'わさびではなく、からし',
      tip: 'もとは船で沖に出るときに鮮度を保つ工夫としてヅケにしていた。八丈島ではハマトビウオを、小笠原諸島ではサワラを「春トビ」と呼んで島ずしにして節分の日に食べる風習がある。',
      links: searchLinks('八丈町', '島ずし'),
    },
  },
  {
    id: 'dozeu-nabe',
    level: 11,
    name: 'どぜう鍋',
    shortName: 'どぜう鍋',
    image: '/assets/foods/tokyo/11_dozeu_nabe.webp',
    radius: 70,
    mass: 9.6,
    score: 2048,
    description: '生きたどじょうを丸ごと酒に入れて酔わせ、割り下とともに鉄鍋で煮込む「丸鍋」。ねぎや三つ葉を散らし、好みで山椒をかけて食べる。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/34_4_tokyo.html',
    icon: { label: 'どぜう鍋', shape: 'hotpot', baseColor: '#6b5a3a', accentColor: '#5a8a3a' },
    reward: {
      unlockScore: 22000,
      area: '台東区浅草',
      headline: '鉄鍋に並ぶ丸ごとのどじょう',
      tip: '肉食を禁じていた江戸時代、どじょうは鯉や鰻と同じく力のつく身近な食材だった。ごぼうとともに精のつくものとされ、主に夏に食べた。',
      links: searchLinks('浅草', 'どぜう鍋'),
    },
  },
  {
    id: 'negima-nabe',
    level: 12,
    name: 'ねぎま鍋',
    shortName: 'ねぎま鍋',
    image: '/assets/foods/tokyo/12_negima_nabe.webp',
    radius: 78,
    mass: 11.4,
    score: 4096,
    description: 'まぐろを1センチのそぎ切り、ねぎを3センチのぶつ切りにする。土鍋にだしと調味料を入れ、冷たいうちにねぎときのこを入れて火にかけ、煮立ったらまぐろを入れてさっと煮る。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/34_26_tokyo.html',
    icon: { label: 'ねぎま鍋', shape: 'pot', baseColor: '#8a2f3a', accentColor: '#e8e0c8' },
    reward: {
      unlockScore: 30000,
      area: '葛飾区',
      headline: '捨てていた脂身を食べる工夫',
      tip: '江戸時代、トロと呼ばれる油の多い腹身は客には出せない下品の料理とされていた。この脂身を食べる工夫として、ねぎと醤油でいっしょに火を通したことから生まれた。',
      links: searchLinks('葛飾区', 'ねぎま鍋'),
    },
  },
];

/** 得点は 2 ^ level を基準にしている（GAME_TUNING.score で方式変更可能）。 */
export const tokyoFoodStage: StageDefinition = {
  regionId: 'tokyo',
  regionName: '東京都',
  mode: 'food',
  title: '東京めしマージ',
  theme: {
    primaryColor: '#2b3b55',
    secondaryColor: '#ece7dc',
    accentColor: '#c0392b',
    backgroundColor: '#f8f5ee',
  },
  /** 背景ツアー「東京めぐり」。軸は季節。3月末の六義園から始めて、2月の三原山で終わる。島しょ部も通る。 */
  scenery: {
    tour: tokyoSceneryTour,
  },
  items: attachHulls(FOOD_ITEMS, TOKYO_FOOD_HULLS),
};
